import { useState } from "react";
import { toast } from "react-hot-toast";
import {
  subscriptionPlanList,
  insertSubscrtiptionPlan,
  updateSubscriptionPlan,
} from "@repo/api-client";
import type {
  InsertSubscriptionRequest,
  UpdateSubscriptionRequest,
  GetSubscriptionRequest,
  SubscriptionPlan,
} from "@repo/types";

// ─── Default States ────────────────────────────────────────────────────────

const initialInsertState: InsertSubscriptionRequest = {
  PlanName: "",
  DurationMonths: 0,
  Price: 0,
  MaxUsers: 0,
  MaxRooms: 0,
  Description: "",
  IsLifetime: false,
  IsActive: true,
};

const initialUpdateState: UpdateSubscriptionRequest = {
  PlanID: 0,
  PlanName: "",
  DurationMonths: 0,
  Price: 0,
  MaxUsers: 0,
  MaxRooms: 0,
  Description: "",
  IsLifetime: false,
  IsActive: true,
};

// ─── Hook ──────────────────────────────────────────────────────────────────

export const useSubscription = () => {
  // Form states
  const [insertRequest, setInsertRequest] =
    useState<InsertSubscriptionRequest>(initialInsertState);

  const [updateRequest, setUpdateRequest] =
    useState<UpdateSubscriptionRequest>(initialUpdateState);

  // List / loading states
  const [subscriptions, setSubscriptions] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(false);

  // ── Reset helpers ──────────────────────────────────────────────────────

  const resetInsertForm = () => setInsertRequest(initialInsertState);

  const resetUpdateForm = () => setUpdateRequest(initialUpdateState);

  // ── Fetch subscription plans ───────────────────────────────────────────

  const fetchSubscriptions = async (
    filters: Partial<GetSubscriptionRequest> = {},
  ) => {
    try {
      setLoading(true);
      const response = await subscriptionPlanList(filters);
      if (response.Success) {
        setSubscriptions(response.Data ?? []);
      } else {
        toast.error(response.Message || "Failed to fetch subscription plans!");
      }
    } catch {
      toast.error("Something went wrong while fetching subscription plans!");
    } finally {
      setLoading(false);
    }
  };

  // ── Insert a new subscription plan ────────────────────────────────────

  const handleInsert = async () => {
    try {
      setLoading(true);
      const response = await insertSubscrtiptionPlan(insertRequest);
      if (response.Success) {
        toast.success(
          response.Message || "Subscription plan created successfully!",
        );
        resetInsertForm();
        await fetchSubscriptions(); // refresh list after insert
      } else {
        toast.error(response.Message || "Failed to create subscription plan!");
      }
    } catch {
      toast.error("Something went wrong while creating the subscription plan!");
    } finally {
      setLoading(false);
    }
  };

  // ── Update an existing subscription plan ──────────────────────────────

  const handleUpdate = async () => {
    try {
      setLoading(true);
      const response = await updateSubscriptionPlan(updateRequest);
      if (response.Success) {
        toast.success(
          response.Message || "Subscription plan updated successfully!",
        );
        resetUpdateForm();
        await fetchSubscriptions(); // refresh list after update
      } else {
        toast.error(response.Message || "Failed to update subscription plan!");
      }
    } catch {
      toast.error("Something went wrong while updating the subscription plan!");
    } finally {
      setLoading(false);
    }
  };

  return {
    // Data
    subscriptions,

    // Insert form
    insertRequest,
    setInsertRequest,
    resetInsertForm,
    handleInsert,

    // Update form
    updateRequest,
    setUpdateRequest,
    resetUpdateForm,
    handleUpdate,

    // Fetch
    fetchSubscriptions,

    // Shared
    loading,
  };
};
